import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { backTargetFromLocation, withBackTarget } from "@/app/back-target";
import { caseDetailPath } from "@/app/routes";
import { Skeleton } from "@/components/ui/skeleton";
import type { CaseListItem } from "@/domain/case/case";
import { ApplicationFilters } from "@/features/my-applications/application-filters";
import { CaseStatusBadge } from "@/features/my-applications/case-status-badge";
import {
  filtersToSearchParams,
  parseFiltersFromSearch,
} from "@/features/my-applications/filters-url";
import { ListPagination } from "@/features/my-applications/list-pagination";
import { isInteractiveTarget } from "@/lib/dom/is-interactive-target";
import { businessDaysBetween, isStuck } from "@/lib/format/business-days";
import { formatDate } from "@/lib/format/datetime";

import { useAllCases } from "./use-all-cases";

const PER_PAGE = 20;

/**
 * 大項目5「全案件モニタ」（管理）。全インスタンスを横断監視し、滞留を見つける。
 *
 * 滞留は現在ステップが 5 営業日を超えて動いていないもの（issue #29 の確定仕様）。
 * 絞り込みはマイ案件と同じ部品を使い、URL を正本にする。
 */
export function AllCasesPage() {
  const { t } = useTranslation(["common", "domain"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const backTo = backTargetFromLocation(useLocation());
  const { filters, page } = parseFiltersFromSearch(searchParams);

  const applyFilters = useCallback(
    (next: CaseListFilters) => {
      setSearchParams(filtersToSearchParams(next, 1), { replace: true });
    },
    [setSearchParams],
  );
  const applyPage = useCallback(
    (next: number) => setSearchParams(filtersToSearchParams(filters, next)),
    [filters, setSearchParams],
  );

  const { data, isPending, isError } = useAllCases({
    filters,
    page,
    perPage: PER_PAGE,
  });

  const items = data?.items ?? [];
  const kpi = {
    inProgress: items.filter((item) => item.status === "inProgress").length,
    stuck: items.filter((item) => isStuck(stuckSince(item))).length,
    returned: items.filter((item) => item.status === "returned").length,
    completed: items.filter((item) => item.status === "completed").length,
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t("common:allCases.title")}</h1>
        <p className="text-muted-foreground text-sm">
          {t("common:allCases.description")}
        </p>
      </header>

      {isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            testId="kpi-in-progress"
            label={t("common:allCases.kpi.inProgress")}
            count={kpi.inProgress}
          />
          <KpiTile
            testId="kpi-stuck"
            label={t("common:allCases.kpi.stuck")}
            count={kpi.stuck}
            danger={kpi.stuck > 0}
          />
          <KpiTile
            testId="kpi-returned"
            label={t("common:allCases.kpi.returned")}
            count={kpi.returned}
          />
          <KpiTile
            testId="kpi-completed"
            label={t("common:allCases.kpi.completed")}
            count={kpi.completed}
          />
        </div>
      )}

      <ApplicationFilters filters={filters} onChange={applyFilters} />

      {isError ? (
        <p className="text-destructive text-sm">{t("common:state.error")}</p>
      ) : null}

      {isPending ? <Skeleton className="h-40 w-full" /> : null}

      {data ? (
        items.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("common:allCases.empty")}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-left text-xs">
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:caseNumber")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:case")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:applicant")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:status")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:currentStep")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("common:allCases.elapsed")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:submittedAt")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <CaseRow
                      key={item.caseId}
                      item={item}
                      onSelect={() =>
                        void navigate(
                          withBackTarget(
                            caseDetailPath(item.caseId, item.processModelId),
                            backTo,
                          ),
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={page}
              pages={data.pagination.pages}
              onChange={applyPage}
            />
          </>
        )
      ) : null}
    </section>
  );
}

function CaseRow({
  item,
  onSelect,
}: {
  readonly item: CaseListItem;
  readonly onSelect: () => void;
}) {
  const { t } = useTranslation("common");
  const since = stuckSince(item);
  const stuck = isStuck(since);
  const elapsed =
    since === null ? null : businessDaysBetween(since, new Date());

  return (
    <tr
      // 行全体で詳細を開く（キーボード操作は案件番号のボタンが担う）
      onClick={(event) => {
        if (isInteractiveTarget(event.target)) return;
        onSelect();
      }}
      className="hover:bg-muted/40 border-border cursor-pointer border-b last:border-0"
    >
      <td className="px-3 py-2 tabular-nums">
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={onSelect}
        >
          {item.caseNumber}
        </button>
      </td>
      <td className="px-3 py-2">{item.title}</td>
      <td className="px-3 py-2">{item.initiator ?? "—"}</td>
      <td className="px-3 py-2">
        <CaseStatusBadge status={item.status} />
      </td>
      <td className="px-3 py-2">{item.currentStep?.label ?? "—"}</td>
      <td
        className={`px-3 py-2 tabular-nums ${
          stuck ? "text-destructive font-semibold" : "text-muted-foreground"
        }`}
      >
        {elapsed === null
          ? "—"
          : t("allCases.businessDays", { count: elapsed })}
      </td>
      <td className="text-muted-foreground px-3 py-2 tabular-nums">
        {formatDate(item.submittedAt)}
      </td>
    </tr>
  );
}

function KpiTile({
  testId,
  label,
  count,
  danger = false,
}: {
  readonly testId: string;
  readonly label: string;
  readonly count: number;
  readonly danger?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      className="border-border bg-card flex flex-col gap-1 rounded-lg border p-4"
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span
        className={`text-2xl font-semibold tabular-nums ${
          danger ? "text-destructive" : ""
        }`}
      >
        {count}
      </span>
    </div>
  );
}

/**
 * 滞留の起点。完了した案件は滞留対象外にする。
 * 現在ステップの開始時刻は一覧 API に無いため、最終更新時刻で近似する。
 */
function stuckSince(item: CaseListItem): Date | null {
  if (item.status === "completed" || item.status === "returned") return null;
  return item.updatedAt ?? item.submittedAt;
}
